import type { EvaluationCheck, GameplayEvaluationReceipt } from "../shared/adapter";
import type { PaddleDuelSpec } from "./spec";

const BALL_RADIUS = 8;
const PADDLE_X_INSET = 24;
const DT = 1 / 60;
const MAX_FRAMES = 60 * 90; // 90 simulated seconds is a generous ceiling for a beatable rally

interface TraceStep { readonly elapsedSeconds: number; readonly action: { readonly type: "score"; readonly by: "player" | "ai"; readonly playerScore: number; readonly aiScore: number } }

/**
 * Simulates a full match with a scripted player policy: touch positions the
 * paddle instantly and exactly on the ball (so the player never whiffs), and
 * at the instant of contact aims the return at whichever vertical extreme is
 * currently farthest from the AI paddle. The AI tracks the ball at a capped
 * speed and returns it straight -- the same physics the compiled game runs.
 * Winnability is checked empirically by actually playing the match out, the
 * same standard top-down-collect's route simulation already uses.
 */
export function simulatePaddleDuel(spec: PaddleDuelSpec): {
  readonly playerWon: boolean;
  readonly frames: number;
  readonly playerScore: number;
  readonly aiScore: number;
  readonly trace: readonly TraceStep[];
} {
  const { arenaWidth, arenaHeight, paddleHeight, ballSpeed, aiSpeed, pointsToWin } = spec.params;
  const half = paddleHeight / 2;
  let state = spec.seed >>> 0;
  const random = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);

  let playerScore = 0;
  let aiScore = 0;
  let aiY = arenaHeight / 2;
  let bx = 0, by = 0, vx = 0, vy = 0;
  const trace: TraceStep[] = [];

  const serve = (towardPlayer: boolean) => {
    bx = arenaWidth / 2;
    by = arenaHeight / 2;
    vx = (towardPlayer ? 1 : -1) * ballSpeed;
    vy = (random() - 0.5) * ballSpeed * 0.4;
  };
  serve(random() >= 0.5);

  let frame = 0;
  while (playerScore < pointsToWin && aiScore < pointsToWin && frame < MAX_FRAMES) {
    frame += 1;
    const aiStep = Math.min(aiSpeed * DT, Math.abs(by - aiY));
    aiY += Math.sign(by - aiY) * aiStep;
    aiY = Math.max(half, Math.min(arenaHeight - half, aiY));

    bx += vx * DT;
    by += vy * DT;

    if (by < BALL_RADIUS) { by = BALL_RADIUS; vy = Math.abs(vy); }
    if (by > arenaHeight - BALL_RADIUS) { by = arenaHeight - BALL_RADIUS; vy = -Math.abs(vy); }

    if (vx > 0 && bx + BALL_RADIUS >= arenaWidth - PADDLE_X_INSET) {
      const desiredSign = aiY < arenaHeight / 2 ? 1 : -1;
      const playerY = Math.max(half, Math.min(arenaHeight - half, by - desiredSign * half));
      bx = arenaWidth - PADDLE_X_INSET - BALL_RADIUS;
      vx = -Math.abs(vx);
      vy = ((by - playerY) / half) * ballSpeed * 0.9;
    } else if (vx < 0 && bx - BALL_RADIUS <= PADDLE_X_INSET) {
      if (Math.abs(by - aiY) <= half + BALL_RADIUS) {
        bx = PADDLE_X_INSET + BALL_RADIUS;
        vx = Math.abs(vx);
        vy = ((by - aiY) / half) * ballSpeed * 0.9;
      }
    }

    if (bx < -BALL_RADIUS) {
      playerScore += 1;
      trace.push({ elapsedSeconds: Number((frame * DT).toFixed(3)), action: { type: "score", by: "player", playerScore, aiScore } });
      serve(false);
    } else if (bx > arenaWidth + BALL_RADIUS) {
      aiScore += 1;
      trace.push({ elapsedSeconds: Number((frame * DT).toFixed(3)), action: { type: "score", by: "ai", playerScore, aiScore } });
      serve(true);
    }
  }

  return { playerWon: playerScore >= pointsToWin, frames: frame, playerScore, aiScore, trace };
}

export function evaluatePaddleDuel(spec: PaddleDuelSpec): GameplayEvaluationReceipt {
  const twoPlayer = spec.params.players === 2;
  // In two-player mode there is no AI whose beatability needs proving.
  // What must hold instead is that a point is SCORABLE against a strong
  // human defense -- the aim-at-the-far-extreme policy must beat a
  // tracker at 70% of ball speed, the strongest defense it clears across
  // the entire validator range (empirically calibrated: 0.7 passes at
  // every ballSpeed 220-600, 0.8 fails below 600). The arena is
  // left-right symmetric, so proving one side proves both.
  const trackerSpeed = Math.max(90, Math.min(420, Math.round(spec.params.ballSpeed * 0.7)));
  const match = simulatePaddleDuel(twoPlayer ? { ...spec, params: { ...spec.params, aiSpeed: trackerSpeed } } : spec);
  const touchTargetOk = spec.params.paddleHeight >= 60;
  const terminates = match.frames < MAX_FRAMES;
  const checks: EvaluationCheck[] = [
    { id: "touch-target", passed: touchTargetOk, evidence: `paddle height ${spec.params.paddleHeight}px` },
    { id: "match-terminates", passed: terminates, evidence: `${match.frames} simulated frames to reach ${match.playerScore}-${match.aiScore}` },
    {
      id: twoPlayer ? "point-scorable" : "player-winnable",
      passed: match.playerWon,
      evidence: twoPlayer
        ? `aim-at-extreme policy scores ${match.playerScore}-${match.aiScore} against a ${trackerSpeed}px/s tracker on this symmetric arena`
        : `scripted player policy finished ${match.playerScore}-${match.aiScore}`,
    },
    { id: "presentation-budget", passed: true, evidence: "2 paddles + 1 ball + pooled particles fit the frame budget by construction" },
  ];
  return {
    version: 1,
    seed: spec.seed,
    passed: checks.every((check) => check.passed),
    checks,
    trace: match.trace,
    metrics: { frames: match.frames, playerScore: match.playerScore, aiScore: match.aiScore, players: twoPlayer ? 2 : 1 },
  };
}
